/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, OnDestroy, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe, Location } from '@angular/common';

import { UntypedFormControl, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { PostService } from '@src/app/modules/social/post/post.service';
import { UserService } from '@src/app/modules/user/user.service';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { Post } from '@src/app/model/post';
import { User } from '@src/app/model/user';
import { required, minChars, minWords } from '@src/app/shared/validators';

import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { NewButtonService } from '@src/app/shared/new-button/new-button.service';
import { Observable, Subscription } from 'rxjs';
import { AppPicklistControl, FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';

import { Privilege } from '@src/app/model/role';
import { Forum, isPublicChannel } from '@src/app/model/forum';
import { Field } from '@src/app/shared/field/Field';
import { ChatComponent } from '@src/app/shared/chat/chat-component/chat.component';
import { ChatField } from '@src/app/shared/chat/chat-field';
import { uuid } from '@src/app/model/abstract-object';
import { FromNowPipe } from '@src/app/shared/pipes/from-now.pipe';
import { CtlHolderComponent } from '@src/app/shared/form/ctl-holder/ctl-holder.component';
import { FormActionsComponent } from '@src/app/shared/form/form-actions/form-actions.component';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { FormErrorComponent } from '@src/app/shared/form/form-error/form-error.component';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormPicklistComponent as FormPicklistComponent_1 } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AvatarComponent } from '@src/app/modules/user/avatar/avatar.component';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { IsNarrowService } from '@src/app/shared/is-narrow.service';
import { AttachmentGridComponent } from '@src/app/shared/form/file/attachment-grid/attachment-grid.component';
import { CommsTemplateService } from '@src/app/modules/social/comms-template-page/comms-templates.service';
import { CommsTemplate, CommsTemplateType } from '@src/app/model/CommsTemplate';
import { MyInjector } from '@src/app/app.module';
import { MatDialog } from '@angular/material/dialog';
import { CommsTemplateUpdaterComponent } from '@src/app/modules/social/comms-template-page/comms-template-updater/comms-template-updater.component';
import { Area } from '@src/app/model/area';
import { AreaService } from '@src/app/modules/unit/area.service';
import { FormPillComponent } from '@src/app/shared/form/form-pill/form-pill.component';
import { makeForumChannelPillField, showChannel } from '@src/app/modules/social/channelPillFields';
import { PostPageNavRoute } from '@src/app/routes/app-router';
import { UnitType } from '@src/app/model/unit-type';
import { UnitTypeService } from '@src/app/modules/unit/unit-type-page/unit-type.service';
import { MAX_MAIL_ATTACHMENT_BYTES } from '@src/app/shared/mail-attachment-chips/mail-attachment-chips.component';

@Component({
    selector: 'app-post-item',
    templateUrl: './post-item.component.html',
    styleUrls: ['./post-item.component.scss'],
    imports: [MatButtonModule, MatIconModule, MatCardModule, AvatarComponent, MatTooltipModule, CtlHolderComponent,
        FormPicklistComponent_1, MatFormFieldModule, MatInputModule, FormsModule, ReactiveFormsModule, FormPillComponent,
        FormErrorComponent, EditorComponent, FormActionsComponent, CtlHolderComponent, FromNowPipe, DatePipe]
})
//extends AbstractFormComponent
export class PostItemComponent implements OnDestroy {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    protected confirmDialogService = inject(ConfirmDialogService);
    postService = inject(PostService);
    protected userSvc = inject(UserService);
    private cds = inject(ConfirmDialogService);
    private currentUserService = inject(CurrentUserService);
    screenSvc = inject(IsNarrowService);
    private commsTmplSvc = inject(CommsTemplateService);
    private areaSvc = inject(AreaService);


    focusItem: Post = new Post();
    currentUser: User = new User();
    privilege: Privilege = new Privilege();
    formMode = false;
    isNew = false;
    subscriptions : Subscription[] = [];

    areas: Area[] = [];
    users: User[] = [];

    chatField: ChatField = ChatComponent.make(this.postService, { formRow: 2, visible: Field.noShow });
    chatControl = this.chatField.makeControl();

    forumField = FormPicklistComponent.make('Channel To Post This Entry Into', 'forum', null,
        { items: [] }, { validators: [required] }
    );

    channelField = makeForumChannelPillField().setupControl();

    areaField = FormPicklistComponent.make('Target Area', 'areaId', 'area',
        { refreshes: ['coreId'], items: this.areas, allowSelectNone: true }, { formColumn: 1 });

    coreField = FormPicklistComponent.make('Target Core', 'coreId', null, { items: [], allowSelectNone: true }, {
        formColumn: 1,
        refresh: (o: Area, control: AppPicklistControl) => {
            if (o) {
                control.field.setPicklistItems(o.cores);
                if (control.value && !o.cores.find( c => c.id === control.value)) {
                    control.setValue(null);
                }
            }
        }
    });

    categoryField = FormPicklistComponent.make('Target Category', 'categoryId', null, { items: [], allowSelectNone: true }, {
        formColumn: 1,
    });

    typeField = FormPicklistComponent.make('Target type', 'typeId', 'type', {
        refreshes: [(type: UnitType) => {
            const currentCategoryId = this.categoryField.control.value;
            if (currentCategoryId && !type.categories.find(c => c.id === currentCategoryId)) {
                this.categoryField.control.setValue(null);
            }
            this.categoryField.setPicklistItems(type.categories);
        }], items: [], allowSelectNone: true }, {
            formColumn: 1
        }
    );

    authorField = FormPicklistComponent.make('Author', 'authorId', null, { items: this.users }, { readonly: true });

    public pageForm = new UntypedFormGroup({
        title: new UntypedFormControl('', [required, minChars(12), minWords(3)]),
        body: new UntypedFormControl('', [required, minChars(50), minWords(10)]),
        imageId: new UntypedFormControl(''),
        forum: this.forumField.makeControl(),
        author: this.authorField.makeControl(),
        areaId: this.areaField.makeControl(),
        coreId: this.coreField.makeControl(),
        typeId: this.typeField.makeControl(),
        categoryId: this.categoryField.makeControl(),
    });
    get forumControl(): AppPicklistControl {
        return this.pageForm.get('forum') as AppPicklistControl;
    }
    get areaControl(): AppPicklistControl {
        return this.pageForm.get('areaId') as AppPicklistControl;
    }    
    get coreControl(): AppPicklistControl {
        return this.pageForm.get('coreId') as AppPicklistControl;
    }
    get typeControl(): AppPicklistControl {
        return this.pageForm.get('typeId') as AppPicklistControl;
    }    
    get categoryControl(): AppPicklistControl {
        return this.pageForm.get('categoryId') as AppPicklistControl;
    }
    get titleControl(): UntypedFormControl {
        return this.pageForm.get('title') as UntypedFormControl;
    }
    get bodyControl(): UntypedFormControl {
        return this.pageForm.get('body') as UntypedFormControl;
    }
    
    
    attachmentGrid = AttachmentGridComponent.make(this.postService);
    attachmentControl = this.attachmentGrid.makeControl();
    currentTemplate : CommsTemplate;

    editorOptions = {
         
        min_height: 300,
        menubar: false,
        statusbar: false,
        relative_urls: false,
        remove_script_host: false,
        contextmenu: 'copy cut custom_paste',
         
        // content_css: 'dark',
        // skin: 'oxide-dark', // For Dark themes?
        plugins: [
            'advlist autolink lists link image charmap print preview anchor',
            'searchreplace visualblocks code fullscreen autoresize',
            'insertdatetime media table paste code help wordcount'
        ],
        toolbar:
            'formatselect | chooseTemplate | bold italic superscript subscript | \
      table | outdent indent alignment | \
      bullist numlist | removeformat | link image | undo redo | code | help',
        setup: (ed) => {
            ed.on('init', () => {
                this.pageForm.controls.body.markAsPristine();
            });
            ed.ui.registry.addMenuItem('custom_paste', {
                text: 'Paste',
                icon: 'paste',
                onAction: () => {
                    // Instead of a browser error, show a friendly SaaS-style notification
                    ed.notificationManager.open({
                    text: 'To paste, please press Ctrl + V on your keyboard (or ⌘ + V on Mac).',
                    type: 'info',
                    timeout: 4000 // Automatically disappears after 5 seconds
                    });
                }
            });            
            /* example, adding a group toolbar button */
            ed.ui.registry.addGroupToolbarButton('alignment', {
                icon: 'align-left',
                tooltip: 'Alignment',
                items: 'alignleft aligncenter alignjustify alignright'
            });
            ed.ui.registry.addMenuButton('chooseTemplate', {
                text: 'Template',
                fetch: this.setupTemplateMenu.bind(this, ed),
            });
        }
    };

    postId: uuid = 0;

    imageId = null;

    locationRef: Location;
    currentChannelFilter: uuid;

    protected objectType = 'Post';
    constructor() {
        const route = this.route;
        const location = inject(Location);
        const currentUserService = this.currentUserService;
        const typeSvc = inject(UnitTypeService);
        const nbs = inject(NewButtonService);


        this.locationRef = location; // for back button
        this.subscriptions.push(route.params.subscribe(params => {
            this.currentChannelFilter = params.channelId;
            if (params.itemId === 'NEW') {
                this.onNew();
            } else {
                this.getPost();
            }
        }));
        this.subscriptions.push(
        currentUserService.getCurrentUser().pipe(takeUntilDestroyed()).subscribe((user) => {
            this.currentUser = user
            this.privilege = User.privilege(this.currentUser, 'Post');
            if (this.privilege.put) {
                this.authorField.defaultReadonly = false;
                this.authorField.readonly = false;
            }
            this.forumField.setPicklistItems(currentUserService.getForums('canCreate'));
            if (this.forumField.picklist.items.length > 0) {
                //console.log({ pl: this.forumField.picklist, len: this.forumField.picklist.items.length });
                const newRoot = PostPageNavRoute.newUrl;
                console.log(newRoot);
                nbs.setupButton($localize`Post`, newRoot, true);
            } else {
                console.log('NEver setup new');
            }
        }));
        this.areaSvc.get<Area>(true).subscribe( areas => {
            this.areas = areas
            this.areaField.setPicklistItems(areas);
        })
        typeSvc.get<UnitType>(true).subscribe( types => {
            this.typeField.setPicklistItems(types);
        })
        this.subscriptions.push(this.userSvc.getUsers().subscribe ( users => this.users = users));
    }

    setEditorContent(ed, t: CommsTemplate) {
        this.currentTemplate = t;
        ed.setContent(t.content);
    }
    saveTemplate(ed) {
        const dialog = MyInjector.instance.get(MatDialog);
        dialog.open(CommsTemplateUpdaterComponent, {
            data: {
                type: CommsTemplateType.POST,
                template: this.currentTemplate,
                content: ed.getContent()
            }
        }).afterClosed().subscribe(tmpl => {
            if (tmpl.id !== this.currentTemplate.id) {
                this.currentTemplate = tmpl;
                ed.setContent(tmpl.content);
            }
        });
    }

    setupTemplateMenu(ed, callback) {
        const items = [];
        this.commsTmplSvc.get(true).subscribe((ts: CommsTemplate[]) => {
            ts.forEach(t => {
                if (t.type === CommsTemplateType.POST.id) {
                    items.push({
                        type: 'menuitem',
                        text: (this.currentTemplate?.id === t.id ? '* ' : '') + t.title,
                        onAction: this.setEditorContent.bind(this, ed, t),
                        disabled: this.currentTemplate?.id === t.id
                    })
                }
            });
            const ct = ed.getContent();
            if (ct && ct !== this.currentTemplate?.content) {
                items.push({ type: 'separator' });
                items.push({
                    type: 'menuitem',
                    text: 'Save Template',
                    onAction: this.saveTemplate.bind(this, ed)
                });
            }

            callback(items);
        });
    }

    getPost(): void {
        /* Get Post for display, not all editing fields retrieved */
        this.isNew = false;
        const id = this.route.snapshot.paramMap.get('itemId');
        this.postService.getOne(id, null)
            .subscribe(post => {
                this.loadPost(post);
            });
    }

    loadPost(post: Post) {
        this.focusItem = post ? post : new Post();
        if (post) {
            this.resetForm();
            this.chatField.setValue(post, false);
            this.chatField.chat.allowComments = User.hasForumPriv(this.currentUser, post.forumId, post.teamId, 'canComment');
            this.chatField.chat.allowReplies = this.chatField.chat.allowComments;

            this.attachmentGrid.setValue(post, this.attachmentGrid.readonly);
        } else {
            post = new Post(); // Avoid errors below if no post found
        }
        
        if (post.areaName) {
            post.forum.name += ' ('
            if (post.coreName) {
                post.forum.name += post.coreName + ': '
            }
            post.forum.name += post.areaName + ')'
        }
        if (post.typeName) {
            post.forum.name += ' ('
            if (post.categoryName) {
                post.forum.name += post.categoryName + ': '
            }
            post.forum.name += post.typeName + ')'            
        }
        showChannel(this.channelField, post.forum);
        this.attachmentGrid.showSimple = true;
        this.attachmentGrid.showGrid = false;
        if (this.canEditPost() && !this.screenSvc.isPhone()) {
            this.attachmentGrid.readonly = false;
            this.attachmentGrid.show();
        } else {
            this.attachmentGrid.readonly = true;
            if (post?.attachments?.length <= 0) {
                this.attachmentGrid.hide();
            }
        }
    }

    private selectedChannel(): Forum {
        return this.forumField.picklist.items.find(f => f.id === this.forumField.control?.value) as Forum;
    }

    canNotify() {
        return this.canEditPost() && this.focusItem?.mailsSent === 0 && !isPublicChannel(this.selectedChannel());
    }

    canEditPost() {
        if (this.focusItem?.id === null) {
            return false; // no edit button needed on new ones, or before date retrieved...
        }
        return User.canEditPost(this.currentUser, this.focusItem);
    }

    get attachmentBytes(): number {
        return (this.focusItem?.attachments ?? []).reduce((sum, a) => sum + (a?.size ?? 0), 0);
    }

    /** Attachments too big to ride along with the notification emails — see MAX_MAIL_ATTACHMENT_BYTES. */
    get attachmentsExceedMailLimit(): boolean {
        return this.attachmentBytes > MAX_MAIL_ATTACHMENT_BYTES;
    }

    get attachmentBytesMb(): string {
        return (this.attachmentBytes / (1024 * 1024)).toFixed(1);
    }

    canDeletePost() {
        if (this.focusItem.id && this.currentUser.currentRole.isAdmin && !this.focusItem.mailsSent) {
            return true;
        }
        return false;
    }

    canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
        if (this.focusItem && this.hasChanges()) {
            return confirm($localize`Your data has not been saved. Are you sure you want to leave and lose these changes?`);
        }
        return true;
    }

    hasChanges(): boolean {
        let hasChanges = false;

        for (const controlName of Object.keys(this.pageForm.controls)) {
            if (this.pageForm.controls[controlName].dirty) {
                hasChanges = true;
            }
        }

        return hasChanges;
    }

    resetForm() {

        this.postId = this.focusItem.id;

        this.pageForm.controls.forum.setValue(this.focusItem.forumId);
        this.pageForm.controls.forum.markAsPristine();

        this.pageForm.controls.author.setValue(this.focusItem.authorId);
        this.pageForm.controls.author.markAsPristine();

        //this.pageForm.controls.title.setValue(this.focusItem.title.raw ? this.focusItem.title.raw : this.focusItem.title.rendered);
        this.pageForm.controls.title.setValue(this.focusItem.title);
        this.pageForm.controls.title.markAsPristine();

        this.pageForm.controls.areaId.setValue(this.focusItem.areaId);
        this.pageForm.controls.areaId.markAsPristine();

        if (this.focusItem.areaId) {
            this.coreField.refresh(this.focusItem.area, this.pageForm.controls.coreId as AppPicklistControl);
        }

        this.pageForm.controls.coreId.setValue(this.focusItem.coreId);
        this.pageForm.controls.coreId.markAsPristine();

        this.pageForm.controls.typeId.setValue(this.focusItem.typeId);
        this.pageForm.controls.typeId.markAsPristine();

        this.pageForm.controls.categoryId.setValue(this.focusItem.categoryId);
        this.pageForm.controls.categoryId.markAsPristine();

        //this.pageForm.controls.body.setValue(this.focusItem.content.raw ? this.focusItem.content.raw : this.focusItem.content.rendered);
        this.pageForm.controls.body.setValue(this.focusItem.content);
        this.pageForm.controls.body.markAsPristine();

        this.pageForm.controls.imageId.setValue(this.focusItem.imageId);
        this.pageForm.controls.imageId.markAsPristine();

        this.imageId = this.focusItem.imageId;

        this.pageForm.markAsPristine();

    }

    onNew(): void {
        this.formMode = true;
        this.isNew = true;
        const p = new Post();
        p.authorId = this.currentUserService.currentUser.id;
        this.loadPost(p);
    }

    notifyUsers() {
        const f: Forum = this.focusItem.forum;
        let msg = $localize`Send a notification email with a copy of your post to everyone in ${f.name} now?`;
        if (f.residents) {
            msg = $localize`Send a notification email with a copy of your post to all residents of ` + this.currentUser.currentTeam.name + ' now?';
        } else if (f.owners) {
            msg = $localize`Send a notification email with a copy of your post to all unit owners of ` + this.currentUser.currentTeam.name + ' now?';
        }

        const doNotify = () => {
            this.focusItem.mailsSent = -1; // Immediately disable the button
            this.postService.notify(this.focusItem);
        };

        if (this.attachmentsExceedMailLimit) {
            this.cds.openChoice({
                title: $localize`Send Emails - WITHOUT Attachments`,
                warning: true,
                msg: $localize`This post has ${this.attachmentBytesMb} MB of attachments, which is over the 9.5 MB limit for notification emails. The attachments will NOT be sent — recipients will see the file names listed in the email but will not be able to open them. Are you sure you want to send the notification without the attachments?`,
                options: [{ name: $localize`Send without attachments`, action: doNotify }],
            });
            return;
        }

        this.cds.open($localize`Send Emails to everyone`, msg, doNotify);
    }

    onEdit(): void {
        /* Make sure we have latest copy for Editing, including extra fields */
        this.postService.getOne(this.postId, null).subscribe(post => {
            if (post) {
                this.formMode = true;
                this.loadPost(post);
            }
        });
    }

    cancelChanges() {
        this.formMode = false;
        this.resetForm();
    }

    saveChanges(): void {
        const update: Post = new Post();
        //update.title.raw = this.pageForm.controls.title.value;
        //update.content.raw = this.pageForm.controls.body.value;
        update.title = this.pageForm.controls.title.value;
        update.content = this.pageForm.controls.body.value;
        update.imageId = this.imageId;
        update.forumId = this.pageForm.controls.forum.value;
        update.authorId = this.pageForm.controls.author.value;
        update.areaId = this.pageForm.controls.areaId.value;
        update.coreId = this.pageForm.controls.coreId.value;
        update.typeId = this.pageForm.controls.typeId.value;
        update.categoryId = this.pageForm.controls.categoryId.value;
        //update.status = 'publish';


        if (this.isNew) {
            /* New Item */
            this.postService.post(update).subscribe(response => {
                if (response) {
                    this.formMode = false;
                    this.resetForm();
                    this.router.navigate([PostPageNavRoute.getIdUrl(response.id)]);
                }
            });
        } else {
            update.id = this.postId;
            update.revision = this.focusItem.revision;
            this.postService.put(update).subscribe(response => {
                this.loadPost(response);
                this.formMode = false;
            });
        }
    }

    imageChanged(id) {

        this.imageId = id;
        this.pageForm.controls.imageId.setValue(id);
        this.pageForm.controls.imageId.markAsDirty();
    }

    goToList() {
        const parms = this.currentChannelFilter ? {channelId: this.currentChannelFilter} : {};
        this.router.navigate([PostPageNavRoute.getListRouteName(), parms]);
    }

    deleteObject(): void {
        this.confirmDialogService.open($localize`Delete this post`,
            $localize`Are you sure you want to delete this item and send it to the recycle bin`,
            (this.doDelete).bind(this),
            $localize`Delete`);
    }

    doDelete(): void {
        this.postService.delete<Post>(this.focusItem).subscribe(response => {
            if (response) {
                this.goToList();
            }
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach( s => s.unsubscribe());
    }
}
